<template>
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="mb-6">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="bx bx-money text-blue-600 mr-2 text-2xl"></i>
                Agent Deposit Form
            </h3>
            <p class="text-sm text-gray-600 mt-1">Make a deposit for Chapman Xpress agents</p>
        </div>

        <form @submit.prevent="showConfirmModal">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Agent Selection -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        <i class="bx bx-user mr-1"></i>
                        Select Agent <span class="text-red-500">*</span>
                    </label>
                     
                    <multiselect
                        v-model="form.agent"
                        :options="agents"
                        label="entity_name"
                        placeholder="Search and select an agent"
                        :custom-label="labelWithID"
                        :class="{
                            'border-red-500': validationErrors.agent
                        }"
                    >
                        <template slot="singleLabel" slot-scope="{ option }">
                            <div class="flex items-center">
                                <div class="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                                    <i class="bx bx-user text-blue-600"></i>
                                </div>
                                <div>
                                    <strong class="text-gray-900">{{ option.entity_name }}</strong>
                                    <span class="text-red-600 text-sm ml-2">{{ option.phone }}</span>
                                </div>
                            </div>
                        </template>
                    </multiselect>
                    <p v-if="validationErrors.agent" class="text-red-500 text-xs mt-2 flex items-center">
                        <i class="bx bx-error-circle mr-1"></i>
                        {{ validationErrors.agent }}
                    </p>
                </div>

                <!-- Amount Input -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        <i class="bx bx-money mr-1"></i>
                        Amount (GMD) <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span class="text-gray-500 text-sm">GMD</span>
                        </div>
                        <input
                            v-model="form.amount"
                            type="number"
                            step="0.01"
                            min="500"
                            max="1000000"
                            :class="[
                                'w-full pl-14 pr-4 py-3 border rounded-lg transition-all duration-200',
                                validationErrors.amount 
                                    ? 'border-red-300 focus:ring-2 focus:ring-red-200 focus:border-red-500' 
                                    : 'border-gray-300 focus:ring-2 focus:ring-blue-200 focus:border-blue-500'
                            ]"
                            required
                            placeholder="Enter amount (500 - 1,000,000)"
                        />
                    </div>
                    <p v-if="validationErrors.amount" class="text-red-500 text-xs mt-2 flex items-center">
                        <i class="bx bx-error-circle mr-1"></i>
                        {{ validationErrors.amount }}
                    </p>
                    <p v-else class="text-gray-500 text-xs mt-2">
                        Minimum: GMD 500 | Maximum: GMD 1,000,000
                    </p>
                </div>
            </div>

            <!-- Selected Agent Info -->
            <div v-if="form.agent" class="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 class="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                    <i class="bx bx-info-circle mr-1"></i>
                    Selected Agent Details
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                        <span class="text-blue-700 font-medium">Name:</span>
                        <span class="text-blue-900 ml-2">{{ form.agent.entity_name }}</span>
                    </div>
                    <div>
                        <span class="text-blue-700 font-medium">Phone:</span>
                        <span class="text-blue-900 ml-2">{{ form.agent.phone }}</span>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                    type="button"
                    @click="resetForm"
                    class="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-all"
                    :disabled="isLoading"
                >
                    <i class="bx bx-reset mr-1"></i>
                    Reset
                </button>
                <button
                    type="button"
                    @click="showConfirmModal()"
                    :disabled="isLoading || !form.agent || !form.amount"
                    :class="[
                        'px-6 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2',
                        isLoading || !form.agent || !form.amount
                            ? 'bg-gray-400 cursor-not-allowed text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
                    ]"
                >
                    <i v-if="!isLoading" class="bx bx-check-circle text-lg"></i>
                    <svg v-else class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{{ isLoading ? 'Processing...' : 'Submit Deposit' }}</span>
                </button>
            </div>
        </form>

        <!-- Confirmation Modal -->
        <div v-if="form.agent">
            <confimation
                v-show="isConfirmModalVisible"
                modalHeadline="Confirm Deposit"
                :isLoading="isLoading"
                :confirmMessage="`Are you sure you want to make a deposit of GMD ${formatAmount(form.amount)} for ${form.agent.entity_name}?`"
                @confirmEvent="confirmRequest"
                @close="closeConfirmModal"
            />
        </div>
    </div>
</template>

<script>
import Form from 'vform';
import axios from 'axios';
import Swal from 'sweetalert2';
import moment from "moment";
import Confimation from '../../../components/Confimation.vue';
import JSPM from "../../../../public/JSPrintManager";

export default {
    components: { Confimation },
    props: ['user'],
    data: () => ({
        isLoading: false,
        isConfirmModalVisible: false,
        accounts: [],
        agents: [],
        userHasConfirmed: false,
        print2default: true,
        form: new Form({
            name: '',
            account: null,
            agent: null,
            amount: '',
            idempotency_key: null,
        }),
        validationErrors: {},
    }),

    mounted() {
        this.loadAgents();
        this.onInit();
    },

    methods: {
        labelWithID({ entity_name, phone }) {
            return `${entity_name} — (${phone})`;
        },

        generateIdempotencyKey() {
            const timestamp = Date.now();
            const random = Math.random().toString(36).substring(2, 15);
            return `dep_${random}_${timestamp}`;
        },

        validateForm() {
            const errors = {};

            if (!this.form.agent || !this.form.agent.id) {
                errors.agent = 'Please select an agent';
            }

            if (!this.form.amount) {
                errors.amount = 'Amount is required';
            } else if (isNaN(this.form.amount)) {
                errors.amount = 'Amount must be a valid number';
            } else if (parseFloat(this.form.amount) < 500) {
                errors.amount = 'Deposit amount must be at least 500';
            } else if (parseFloat(this.form.amount) > 1000000) {
                errors.amount = 'Deposit amount cannot exceed 1,000,000';
            }

            this.validationErrors = errors;
            return Object.keys(errors).length === 0;
        },

        async loadAgents() {
            try {
                const { data } = await axios.get(`/get/agents`);

                this.agents = data.data;
                return true;
            } catch (e) {
                console.log('Error');
                return false;
            }
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return dateString;
        },

        formatAmount(amount) {
            if (!amount) return '0.00';
            return parseFloat(amount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },

        resetForm() {
            this.form.agent = null;
            this.form.amount = '';
            this.form.idempotency_key = null;
            this.validationErrors = {};
        },

        showConfirmModal() {
            if (!this.validateForm()) {
                return;
            }

            if (!this.form.idempotency_key) {
                this.form.idempotency_key = this.generateIdempotencyKey();
            }

            this.isConfirmModalVisible = true;
        },
        closeConfirmModal() {
            this.isConfirmModalVisible = false;
        },

        confirmRequest(req) {
            console.log('User has confirmed', req);
            this.userHasConfirmed = req;

            if (this.userHasConfirmed == 'yes') {
                this.onSubmit();
            }
        },

        async onSubmit() {
            if (!this.validateForm()) {
                this.isLoading = false;
                this.closeConfirmModal();
                return false;
            }

            this.isLoading = true;

            try {
                const payload = {
                    agent: {
                        id: this.form.agent.id
                    },
                    amount: parseFloat(this.form.amount),
                    idempotency_key: this.form.idempotency_key
                };

                console.log('Sending deposit payload:', payload);

                const { data } = await axios.post('/deposit/agent/cx', payload);

                console.log("Deposit created successfully:", data);

                this.print(data.data);

                this.form.agent = null;
                this.form.amount = '';
                this.form.idempotency_key = null;
                this.validationErrors = {};

                this.isLoading = false;
                this.closeConfirmModal();
                
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Deposit has been successfully created.',
                    timer: 2000
                });

                this.$router.push({ path: '/dashboard' });

                return true;
            } catch (error) {
                this.isLoading = false;
                this.closeConfirmModal();

                console.error('Deposit error details:', {
                    message: error.message,
                    response: error.response,
                    status: error.response?.status,
                    data: error.response?.data
                });

                let errorMessage = 'An error occurred while processing the deposit';

                if (error.response) {
                    const status = error.response.status;
                    const responseData = error.response.data;

                    console.log('Error response data:', responseData);

                    if (status === 500) {
                        errorMessage = responseData?.error || responseData?.message || 'Server error occurred';
                    } else if (status === 401) {
                        errorMessage = 'Authentication failed. Please login again.';
                        Swal.fire({
                            icon: 'error',
                            title: 'Authentication Error',
                            text: errorMessage
                        });
                        this.$router.push({ path: '/login' });
                        return false;
                    } else if (status === 422) {
                        if (responseData?.errors) {
                            const errors = Object.values(responseData.errors).flat();
                            errorMessage = errors.join(', ');
                        } else {
                            errorMessage = responseData?.message || 'Validation error';
                        }
                    } else if (responseData?.error) {
                        errorMessage = responseData.error;
                    } else if (responseData?.message) {
                        errorMessage = responseData.message;
                    }
                } else if (error.request) {
                    errorMessage = 'Network error. Please check your connection and try again.';
                } else {
                    errorMessage = error.message || errorMessage;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Deposit Failed',
                    text: errorMessage
                });

                return false;
            }
        },

        print: function (receipt) {
            console.log("receipt", receipt);

            if (this.selected_printer === "" && !this.print2default) {
                alert("You must select a printer");
                return;
            }

            var cpj = new JSPM.ClientPrintJob();

            if (this.print2default) {
                cpj.clientPrinter = new JSPM.DefaultPrinter();
            } else {
                cpj.clientPrinter = new JSPM.InstalledPrinter(this.selected_printer);
            }

            var day = new Date(receipt.created_at);
            var dateCreated = moment().utc(day).format("Do MMM, YYYY, h:mm:ss a");

            //Set content to print...
            //Set content to print...
            //Create ESP/POS commands for sample label
            var esc = "\x1B"; //ESC byte in hex notation
            var newLine = "\x0A"; //LF byte in hex notation
            var fullCut = esc + "m";

            var cmds = esc + "@"; //Initializes the printer (ESC @)
            cmds += esc + "!" + "\x38"; //Emphasized + Double-height + Double-width mode selected (ESC ! (8 + 16 + 32)) 56 dec => 38 hex
            cmds += "CHAPMAN XPRESS"; //text to print
            cmds += newLine;
            cmds += esc + "!" + "\x00"; //Character font A selected (ESC ! 0)

            cmds += newLine + newLine;

            cmds += "Agent Name: " + this.form.agent.entity_name;
            cmds += newLine;
            cmds += `Amount .: GMD ${receipt.amount / 100}`;
            cmds += newLine + newLine;


           
            cmds += `Deposit Date .:      ${dateCreated}`;

            cmds += newLine + newLine;
            cmds += esc + "!" + "\x18"; //Emphasized + Double-height mode selected (ESC ! (16 + 8)) 24 dec => 18 hex
            cmds += `Operator: ${this.user.data.name}`;
            cmds += newLine;
            cmds += esc + "!" + "\x00"; //Character font A selected (ESC ! 0)

            cmds += "Thank you";
            cmds += newLine;

            // cmds += esc + '!' + '\x38';

            cmds += newLine;
            cmds += `Phone: +220 438 3223 - 307 4455 - ${this.user.data.staff.branch_contact_phone}`;
            cmds += newLine + newLine;
            cmds += newLine + newLine;

            cmds += fullCut;

            cpj.printerCommands = cmds;
            //Send print job to printer!
            cpj.sendToClient();

            // this.receipt = null;
        },

        onInit: function () {
            var _this = this;
            JSPM.JSPrintManager.auto_reconnect = true;
            JSPM.JSPrintManager.start();
            JSPM.JSPrintManager.WS.onStatusChanged = function () {
                _this.getPrinters().then((p) => {
                _this.printers = p;
                _this.$nextTick();
                });
        };
    },

    getPrinters: function () {
      return new Promise((ok, err) => {
        let printers = [];
        if (JSPM.JSPrintManager.websocket_status == JSPM.WSStatus.Open) {
          JSPM.JSPrintManager.getPrinters()
            .then(function (myPrinters) {
              printers = myPrinters;
              console.log("printers", printers);
              ok(printers);
            })
            .catch((e) => err(e));
        } else {
          console.warn("JSPM WS not open");
          ok(printers);
        }
      });
    },
    },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
